import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Divider,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { FiCheckCircle } from 'react-icons/fi';
import { Link } from 'react-router-dom';

function TermsAndCondition() {
  return (
    <Box bg="#F8FAFC" minH="100vh" py={{ base: 8, md: 12 }}>
      {/* Header Banner */}
      <Box
        bg="linear-gradient(135deg, #0B132B 0%, #162237 60%, #1E293B 100%)"
        color="white"
        py={{ base: 12, md: 16 }}
        mb={{ base: 8, md: 12 }}
      >
        <Container maxW="5xl" px={{ base: 4, md: 8 }} textAlign="center">
          <Badge
            bg="rgba(234, 179, 8, 0.15)"
            color="#FACC15"
            border="1px solid"
            borderColor="rgba(234, 179, 8, 0.3)"
            px={3.5}
            py={1}
            borderRadius="full"
            fontSize="xs"
            fontWeight="semibold"
            letterSpacing="wider"
            textTransform="uppercase"
            mb={3}
          >
            Institutional Agreement
          </Badge>
          <Heading as="h1" size={{ base: 'xl', md: '2xl' }} fontWeight="extrabold">
            Terms & Conditions of Service
          </Heading>
          <Text fontSize="sm" color="gray.300" mt={2}>
            Governing rules for patrons, advocates, subscribers, and institutions utilizing Navneet Law Studios
          </Text>
        </Container>
      </Box>

      {/* Main Document Content */}
      <Container maxW="5xl" px={{ base: 4, md: 8 }}>
        <Box
          bg="white"
          p={{ base: 6, md: 12 }}
          borderRadius="2xl"
          border="1px solid"
          borderColor="gray.100"
          boxShadow="sm"
        >
          <VStack spacing={7} align="stretch" color="gray.700" fontSize="sm" lineHeight="tall">
            <Box>
              <Text fontSize="md" color="gray.800" fontWeight="medium">
                By browsing, registering, or transacting on <strong>www.navneetlawstudio.com</strong>, you (hereinafter referred to as the "User", "Advocate", or "Subscriber") explicitly agree to be bound by the following Terms & Conditions. Navneet Law Studio Pvt. Ltd. reserves the right to alter or update these terms without individual prior notification.
              </Text>
            </Box>

            <Divider borderColor="gray.100" />

            <Box>
              <Heading as="h2" size="sm" color="#162237" mb={3} display="flex" alignItems="center" gap={2}>
                <Box w="3px" h="16px" bg="#EAB308" borderRadius="full" />
                1. Intellectual Property & Copyright Protection
              </Heading>
              <Text>
                All content, website architecture, editorial headnotes, design tokens, software UI integrations, and curated databases featured on this platform are the sole proprietary copyright of Navneet Law Studio Pvt. Ltd., its respective publishing associates, and licensed partners (including Supreme Today AI, CDJ Law Journal, Digilegal, Taxmann, LegitQuest, and DLT). Unauthorized commercial extraction, electronic mirroring, or automated crawling is strictly prohibited by law.
              </Text>
            </Box>

            <Divider borderColor="gray.100" />

            <Box>
              <Heading as="h2" size="sm" color="#162237" mb={3} display="flex" alignItems="center" gap={2}>
                <Box w="3px" h="18px" bg="#EAB308" borderRadius="full" />
                2. User License & Limitations on Content Usage
              </Heading>
              <Text mb={2}>
                Subscribers and purchasers receive a non-exclusive, non-transferable license to access purchased law commentaries and software subscriptions for personal, judicial, or internal law firm practice. You agree not to:
              </Text>
              <List spacing={2} pl={2}>
                <ListItem display="flex" alignItems="center">
                  <ListIcon as={FiCheckCircle} color="#059669" />
                  Systematically scrape, archive, or syndicate search outputs to any external repository, bulletin, or competing legal engine.
                </ListItem>
                <ListItem display="flex" alignItems="center">
                  <ListIcon as={FiCheckCircle} color="#059669" />
                  Remove, conceal, or modify statutory copyright, trade name, or watermark identifiers.
                </ListItem>
                <ListItem display="flex" alignItems="center">
                  <ListIcon as={FiCheckCircle} color="#059669" />
                  Resell or sub-license multi-seat cloud software keys to unauthorized third parties.
                </ListItem>
              </List>
            </Box>

            <Divider borderColor="gray.100" />

            <Box>
              <Heading as="h2" size="sm" color="#162237" mb={3} display="flex" alignItems="center" gap={2}>
                <Box w="3px" h="18px" bg="#EAB308" borderRadius="full" />
                3. Account Credentials & Security
              </Heading>
              <Text>
                Users are solely responsible for maintaining the confidentiality of their login credentials, passwords, and API auth tokens. Any unauthorized activity originating from your account must be brought to our immediate notice at <strong>customercare@navneetlawstudio.com</strong>.
              </Text>
            </Box>

            <Divider borderColor="gray.100" />

            <Box>
              <Heading as="h2" size="sm" color="#162237" mb={3} display="flex" alignItems="center" gap={2}>
                <Box w="3px" h="18px" bg="#EAB308" borderRadius="full" />
                4. Cross-Reference Policies & Statutory Disclaimers
              </Heading>
              <Text mb={2}>
                Our full relationship with you is further governed by the following companion legal policies:
              </Text>
              <HStack spacing={4} wrap="wrap">
                <Link to="/privacy" style={{ color: '#162237', fontWeight: 600, textDecoration: 'underline' }}>
                  Privacy & Data Protection Policy
                </Link>
                <Link to="/disclaimer" style={{ color: '#162237', fontWeight: 600, textDecoration: 'underline' }}>
                  Publication & Software Disclaimer
                </Link>
                <Link to="/shippingpolicy" style={{ color: '#162237', fontWeight: 600, textDecoration: 'underline' }}>
                  Shipping & Return Policy
                </Link>
              </HStack>
            </Box>

            <Divider borderColor="gray.100" />

            <Box>
              <Heading as="h2" size="sm" color="#162237" mb={3} display="flex" alignItems="center" gap={2}>
                <Box w="3px" h="18px" bg="#EAB308" borderRadius="full" />
                5. Limitation of Liability & Force Majeure
              </Heading>
              <Text>
                Navneet Law Studio shall not be liable for any direct, indirect, incidental, or consequential damages resulting from inability to access this portal or temporary judicial downtime. In all cases permitted by law, our aggregate maximum liability for any claim shall not exceed the amount actually paid by you for the specific book or software subscription in dispute.
              </Text>
            </Box>

            <Divider borderColor="gray.100" />

            <Box>
              <Heading as="h2" size="sm" color="#162237" mb={3} display="flex" alignItems="center" gap={2}>
                <Box w="3px" h="18px" bg="#EAB308" borderRadius="full" />
                6. Governing Law & Dispute Jurisdiction
              </Heading>
              <Text>
                All disputes, interpretations, or claims arising under this agreement shall be governed by the laws of India and subject to the exclusive jurisdiction of the competent Courts and Tribunals situated at Nagpur / New Delhi only.
              </Text>
            </Box>
          </VStack>
        </Box>
      </Container>
    </Box>
  );
}

export default TermsAndCondition;
